import type { ChangeEvent, Ref } from 'react';
import { useEffect, useState, useTransition } from 'react';

import { SearchIcon, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Input, Spinner } from '@/_components';
import { noop, useDebounce } from '@/_shared';

import { ActionButton } from '../actions';
import {
  DATA_TABLE_FIRST_ROW_SELECTOR,
  DATA_TABLE_TOOLBAR_KEYBOARD_NAV_ATTR,
  focusFirstRowInToolbarNavRoot,
} from '../data-table/toolbar-keyboard-nav/utils';
import { ActionButtonContainer } from '../layout';

export function DebouncedInput({
  onChange,
  placeholder,
  debounce = 500,
  label = 'Search',
  value: initialValue,
  showSearchIcon = false,
  synchExternalValue = true,
  containerClassName = '',
  onKeyDown,
  focused = false,
  inputRef,
}: {
  value: string | number;
  debounce?: number;
  label?: string;
  containerClassName?: string;
  showSearchIcon?: boolean;
  placeholder?: string;
  synchExternalValue?: boolean;
  onChange: (value: string | number) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  focused?: boolean;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const [query, setQuery] = useState(initialValue);
  const debouncedQuery = useDebounce(query, debounce);
  const [isPending, startTransition] = useTransition();

  // Sync external value if requested
  useEffect(() => {
    if (synchExternalValue) {
      setQuery(initialValue);
    }
  }, [initialValue, synchExternalValue]);

  // Debounce propagate changes
  useEffect(() => {
    startTransition(() => {
      onChange(debouncedQuery);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
      if (e.defaultPrevented) return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const target = e.currentTarget as HTMLElement;
      const navRoot = target.closest(`[${DATA_TABLE_TOOLBAR_KEYBOARD_NAV_ATTR}]`);
      const firstRowSelector = navRoot?.getAttribute('data-first-row-selector') ?? DATA_TABLE_FIRST_ROW_SELECTOR;
      if (focusFirstRowInToolbarNavRoot(navRoot, firstRowSelector)) {
        return;
      }
      const selector = [
        'a[href]',
        'area[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      const focusable = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(
        (el) =>
          !el.hasAttribute('disabled') &&
          !el.getAttribute('aria-hidden') &&
          el.tabIndex !== -1 &&
          !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
      );
      const idx = focusable.indexOf(target);
      if (idx > -1) {
        const next = focusable[idx + 1];
        if (next) next.focus();
      }
    }
  };

  return (
    <section className={twMerge('flex w-full flex-nowrap items-start justify-end', containerClassName)}>
      <Input
        ref={inputRef}
        value={query}
        placeholder={placeholder}
        label={label}
        onChange={handleOnChange}
        className="placeholder:text-xs placeholder:opacity-40 focus:placeholder:opacity-40"
        labelProps={{
          className: showSearchIcon
            ? `${query}`.length > 0 || isPending
              ? 'w-[calc(100%_+_3.5rem)]'
              : 'w-[calc(100%_+_1.75rem)]'
            : `${query}`.length > 0 || isPending
              ? 'w-[calc(100%_+_1.75rem)]'
              : '',
        }}
        onKeyDown={handleKeyDown}
        autoFocus={focused}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        tabIndex={1}
      />

      {(`${query}`.length > 0 || isPending) && (
        <ActionButtonContainer>
          {isPending ? (
            <Spinner size="xs" />
          ) : (
            <ActionButton
              title="Clear Search"
              icon={X}
              onClick={() => {
                onChange('');
                setQuery('');
              }}
            />
          )}
        </ActionButtonContainer>
      )}

      {showSearchIcon && (
        <ActionButtonContainer>
          <ActionButton title="Search" icon={SearchIcon} onClick={noop} />
        </ActionButtonContainer>
      )}
    </section>
  );
}
